/**
 * MONSTER TOWN — PIECE ICONS. OWNED BY THE BUILD CREW (src/build/**).
 *
 * The drawings themselves now live in `src/render/icons2d.ts` — the shared art
 * layer — because the HUD crew's shop panel needs the SAME picture of the same
 * piece as the piece drawer, and crews never import each other. Nothing about
 * this module's public surface changed: `drawPieceIcon`, `drawCategoryIcon` and
 * `drawToolIcon` are still exported from here and still draw exactly what they
 * drew before.
 *
 * ONE DRAWING IS OVERRIDDEN HERE, IN THIS CREW'S FILE: `rotate`.
 * Undo and rotate sat next to each other in the tool group as two circular
 * arrows that differed only by a small teal triangle. A kid reaching for one
 * got the other, and one of them silently deletes the piece they just made.
 * `drawBuildToolIcon('rotate')` puts a CUBE inside the arc — the arrow is now
 * obviously going AROUND A PIECE, which is what rotate does and what undo does
 * not. (`src/render/**` is architect-owned and additive only: its output is
 * left exactly as it was, and shop/HUD icons are unaffected.)
 */

import { css } from '../core/palette'

export { drawCategoryIcon, drawPieceIcon, drawToolIcon } from '../render/icons2d'
import { drawToolIcon as drawSharedToolIcon } from '../render/icons2d'

type ToolName = Parameters<typeof drawSharedToolIcon>[2]

/** A chunky isometric cube with a turning arrow wrapped over the top of it. */
function rotateWithPiece(c: CanvasRenderingContext2D, size: number): void {
  c.clearRect(0, 0, size, size)
  c.save()
  c.scale(size / 100, size / 100)
  c.lineJoin = 'round'
  c.lineCap = 'round'

  // the piece being turned
  const ink = css('ink')
  const face: [string, [number, number][]][] = [
    [css('woodLight'), [[50, 46], [80, 60], [50, 74], [20, 60]]],
    [css('wood'), [[20, 60], [50, 74], [50, 96], [20, 82]]],
    [css('woodDark'), [[50, 74], [80, 60], [80, 82], [50, 96]]],
  ]
  for (const [colour, pts] of face) {
    c.beginPath()
    const first = pts[0] as [number, number]
    c.moveTo(first[0], first[1])
    for (const p of pts.slice(1)) c.lineTo(p[0], p[1])
    c.closePath()
    c.fillStyle = colour
    c.fill()
    c.lineWidth = 4
    c.strokeStyle = ink
    c.stroke()
  }

  // the arrow going around it
  c.strokeStyle = ink
  c.lineWidth = 10
  c.beginPath()
  c.arc(50, 44, 30, Math.PI * 0.92, Math.PI * 2.2)
  c.stroke()
  c.beginPath()
  c.moveTo(60, 4)
  c.lineTo(84, 20)
  c.lineTo(58, 32)
  c.closePath()
  c.fillStyle = css('uiAccent')
  c.fill()
  c.lineWidth = 4
  c.stroke()
  c.restore()
}

/**
 * A fat green tick. "I am finished with this" — the way out of the bin and the
 * way out of holding a piece (see the tool tray in palette.ts). It is drawn here
 * rather than in the shared icon set because it is a BUILD tool: the HUD's own
 * `check` is a different size and lives on panel buttons.
 */
function doneTick(c: CanvasRenderingContext2D, size: number): void {
  c.clearRect(0, 0, size, size)
  c.save()
  c.scale(size / 100, size / 100)
  c.lineJoin = 'round'
  c.lineCap = 'round'
  c.beginPath()
  c.arc(50, 50, 38, 0, Math.PI * 2)
  c.fillStyle = css('uiOk')
  c.fill()
  c.lineWidth = 6
  c.strokeStyle = css('ink')
  c.stroke()
  c.beginPath()
  c.moveTo(30, 52)
  c.lineTo(45, 68)
  c.lineTo(72, 32)
  c.lineWidth = 13
  c.strokeStyle = css('ink')
  c.stroke()
  c.lineWidth = 7
  c.strokeStyle = css('flowerWhite')
  c.stroke()
  c.restore()
}

/** Tool glyphs for the piece drawer. `rotate` and `done` are ours. */
export function drawBuildToolIcon(
  c: CanvasRenderingContext2D,
  size: number,
  tool: ToolName | 'done',
): void {
  if (tool === 'rotate') {
    rotateWithPiece(c, size)
    return
  }
  if (tool === 'done') {
    doneTick(c, size)
    return
  }
  drawSharedToolIcon(c, size, tool)
}
